import { EmptyMessageCustomization } from "@multiweb/frames";
import { OWDataType } from "@multiweb/types/contracts";
import { CaseInsensitiveMap, IOWObject } from "@multiweb/types/multiweb";
import { ConditionParams, IDataSource } from "Global/Base/DataSources/Interfaces";
import { Tuple } from "Global/Base/DataTypes/NTuples";
import { ModelDescriptionsManager } from "OW.Remoting/Remoting/Cache/ClassDescriptionCache/ModelDescriptionsManager";
import { ExtendedPropertiesType } from "UI/types";
import { createElement } from "UI/UI.ElementarControls/Utils/CustomElementUtils";
import { getExtendedPropertiesFromElement } from "UI/utils";
import { DataConverter } from "../../../../../Global/Base/Converters/DataConverter";
import { IDisposable } from "../../../../../Global/Base/IDisposable";
import { ConditionOperator, StringUtils } from "../../../../../Global/Base/Utils/StringUtils";
import { IColumnDefinition } from "../../../../UI.Controls/DataGridSubcontrols/Columns/ColumnFactory";
import { FormControlWithLoggerParams } from "../../Controls/ControlInterfaces";
import { StandardTwoStateAction } from "../Actions/StandardTwoStateAction";
import { XGAction } from "../Actions/XGAction";
import { IActualObjectChangedEventArgs } from "../Frames";
import { SeparateDetailFrame } from "../SeparateDetailFrame";
import { FrameSharedMethods } from "./FrameSharedMethods";
import { IBaseFrame } from "./IBaseFrame";
import { MasterableFrame } from "./MasterableFrame";

export abstract class BaseFrame extends MasterableFrame implements IBaseFrame, IDisposable {
    private _emptyMessageCustomization: EmptyMessageCustomization;
    private _masterAssocParam: Tuple<string, any> = new Tuple("@MASTER_ASSOC_PARAM", undefined); // TODO 30745 po natypování IOWObject.id přiřadit typ
    private _masterClassParam: Tuple<string, string | undefined> = new Tuple("@MASTER_CLASS_PARAM", undefined);
    private __showSubRecordsSupport: boolean | null;
    private _showSubRecordsSupport: boolean = false;
    protected _showSubRecordsAction: StandardTwoStateAction;

    public condition: string;
    public conditionParamsAttrs?: readonly string[];

    public fontColorAttr: string;
    public fontColorFlags: string;

    private _container: HTMLElement;

    public get container(): HTMLElement {
        return this._container;
    }

    protected override _interactive: boolean = false;

    private _captionElement: HTMLElement;
    protected _showCaption: boolean = false;

    public override get renderStandardRowActions(): boolean {
        return true;
    }

    public override set enabled(value: boolean) {
        super.enabled = value;
        this._updateEnabledStatusForWorkflowActions();
    }

    public override get enabled(): boolean {
        return super.enabled;
    }

    public override get interactive(): boolean {
        return this._interactive;
    }

    public override set interactive(interactive: boolean) {
        this._interactive = interactive;
        this._updateEnabledStatusForWorkflowActions();
    }

    public get emptyMessageCustomization(): EmptyMessageCustomization {
        return this._emptyMessageCustomization;
    }

    constructor(params: FormControlWithLoggerParams) {
        super(params);
        const { element } = params;

        element.classList.add("base-frame");

        this._emptyMessageCustomization = FrameSharedMethods.parseEmptyMessageCustomization(this.element);

        this._showCaption = DataConverter.toBoolean(this.element.getAttribute("data-showcaption"), false);
        this._container = this._creteFrameContainer();

        // Všechny elementy, které nejsou elementy práv přesuneme do _containerElement
        Array.from(this.element.children).forEach(e => {
            if (!(e.getAttribute("data-tscitem") && e.getAttribute("data-tscitem").startsWith("Rights.")))
                this.container.appendChild(e); // Přesune element do jiného rodiče
        });

        this.element.appendChild(this.container);
    }

    protected _creteFrameContainer(): HTMLElement {
        return createElement({ type: "div", classes: ["frame-container"] });
    }

    protected abstract _createColumns(columnsDefinition: IColumnDefinition[]): Promise<void>;

    /**
     * Metoda volana pri zmene master zaznamu
     */
    protected abstract _onMasterObjectChanged(masterObjectChangedArgs?: IActualObjectChangedEventArgs): Promise<void>;

    /**
     * Zaktualizuje aktivnost workflow akcí podle aktivnosti framu (readonly a povolení právy)
     */
    protected _updateEnabledStatusForWorkflowActions(): void {
        if (!this.menu)
            return;

        for (const wfAction of this._wfActions) {
            wfAction.enabledVisual = this.interactive;
            wfAction.enabled = this.interactive;
        }
    }

    /** Vytvoří popisek, pokud je zadán lokalizační klíč */
    protected _createFrameCaption(): void {
        if (this.locKey && !this.locKey.endsWith("-666")) { // Element caption chceme vložit do stránky jen pokud je co zobrazit
            this.element.classList.add("with-frame-caption");
            this._captionElement = FrameSharedMethods.createFrameCaptionElement(this.locKey);
            this.element.insertBefore(this._captionElement, this.element.firstElementChild);
            this._setFrameCaptionVisibility(this._showCaption);
        }
    }

    /** Podle showCaption nastavuje viditelnost elementu frame-caption. */
    protected _setFrameCaptionVisibility(value: boolean): void {
        this._captionElement?.classList.toggle("hidden", !value);
    }

    public addCustomActions(): void {
        for (const aktAction of this._actions) {
            if (aktAction.control instanceof XGAction) {
                const action = aktAction.control;
                action.owner = this;

                this.menu.addAction(action);
            }
        }
    }

    /**
     * Vytvoří inicializační podmínku pro datasource seznamu
     * Počítá i s formCondition a právy
     */
    public async createReaderCondition(): Promise<string> {
        let condition = "";

        // jen data hlavniho framu by mela byt omezena podminkou danou pri otevreni formu (podmínka uvedená na ownerForm - predavana napr. z DFA nebo LOVEdit)
        if (this.isMainFrame) {
            if (!this._formCanRead) // pokud je na formuláři tvrdým právem zakázano čtení dat, tak je to řešeno nesplnitelnou podmínkou - sice může docházet ke zbytečným voláním služby, ale bude díky tomu fungovat (i refreshe a pod.)
                return "1=0";

            // přidá se podmínka vazby na nadřízený záznam
            if (this.ownerForm.masterLinkToAttr && this.ownerForm.masterObject && !this.ownerForm.masterObject.isNew)
                condition = this.ownerForm.masterLinkToAttr + "=" + this.ownerForm.masterObject.id; // v masterObject uz je koncovy objekt po uplatneni pripadneho MasterAttr

            // přidá se podmínka čtení pro formulář, pokud je nastavená (má přednost)
            if (this.ownerForm.formCondition)
                condition = StringUtils.combineConditions(ConditionOperator.and, condition, this.ownerForm.formCondition);

            // pokud nejsou parametry pro podmínky zároveň z formu i z framu, tak se může přidat jednoduchá framová podmínka
            if (!this.ownerForm.formConditionParamAttrs || !this.conditionParamsAttrs)
                condition = StringUtils.combineConditions(ConditionOperator.and, condition, this.condition);

            condition = FrameSharedMethods.appendReadRightCondition(condition, this);
        } else { // pro ostatni framy
            if ((this.masterFrame && this.masterFrame.actualObject == null) || !(this.masterFrame?.loadDataForNewObject) && this.masterFrame?.actualObject?.isNew)
                return "1=0"; // nelze načítat záznamy na podřízeném framu, když nemám master záznam nebo je nově vytvořený

            // prida se podminka vazby na nadrizeny zaznam na nadrizenem framu
            const masterId = await this._getMasterObjectId();
            if (masterId) {
                this._masterAssocParam.two = masterId;
                condition = `${this.masterLinkToAttr}=${this._masterAssocParam.one}`;

                if (this.showSubRecords) {
                    condition = `((${condition}) or :DB.ExistSubRecords(${this._masterClassParam.one}, ${this.masterLinkToAttr}, ${this._masterAssocParam.one}))`;
                }
            }

            // přidá se podmínka čtení pro frame
            if (this.condition)
                condition = StringUtils.combineConditions(ConditionOperator.and, condition, this.condition);
        }
        return condition;
    }

    private async _getMasterObjectId(): Promise<any> {
        if (this.isMasterRecordSelected() && this.masterLinkToAttr) {
            if (this.masterAttr) {
                const obj = await this.masterFrame.actualObject.getValue(this.masterAttr);
                if (obj && obj instanceof IOWObject) {
                    this._masterClassParam.two = obj.objectName;
                    return obj.getValue("ID");
                }
            } else {
                this._masterClassParam.two = this.masterFrame.actualObject.objectName;
                return this.masterFrame.actualObject.id;
            }
        } else if (this.readVersioned && this.masterLinkToAttr && this.ownerForm.detailFormObject) { // Verze otevřená z VersionSelection nemá masterFrame, pouze předaný objekt
            this._masterClassParam.two = this.ownerForm.detailFormObject.objectName;
            return this.ownerForm.detailFormObject.id;
        }

        return null;
    }

    /** Určuje, zda je na masterovi vybrán nějaký záznam */
    public isMasterRecordSelected(): boolean {
        return (this.masterFrame && this.masterFrame.actualObject && !this.masterFrame.actualObject.isNew);
    }

    /**
     * Vytvoří kolekci s inicializačními parametry pro datasource seznamu
     * Počítá i s formConditionParams
     * @returns seznam hodnot parametrů pro podmínky nebo null, pokud nebylo možné hodnoty načíst
     */
    protected async _createReaderConditionParams(): Promise<ConditionParams> {
        return FrameSharedMethods.createReaderConditionParams(this);
    }

    /**
     * Vygeneruje nove podminky a parametry pro cteni dat datasourcem
     * @param dataSource datasource, kteremu se maji nastavit nove podminky
     */
    public async updateCondition(dataSource: IDataSource): Promise<void> {
        await this._createConditionAndConditionParams(dataSource);
    }

    /**
     * Vygeneruje nove podminky a parametry pro cteni data datasourcem a zahaji nacitani dat
     * @param dataSource datasource, kteremu se maji nastavit nove podminky
     * @param readNewData priznak ridici zda se maji hned nacist nova data
     */
    protected async _updateConditionAndRead(dataSource: IDataSource, readNewData: boolean = true): Promise<void> {
        if (this.masterFrame &&
        (!this.masterFrame.actualObject || // u podrizeneho framu nema vyznam nacitat data pokud v masteru neni vybran zaznam
            (!(this.masterFrame instanceof SeparateDetailFrame) && this.masterFrame.actualObject.isNew))) { // pokud master neni SeparateDetailFrame a objekt je novy je zbytecne nacitat data {
            this._resolveActualObjectSetDeferred(true); // nebude se dočítat, takže beru actualObject za dočtený
            return;
        }

        await this._createConditionAndConditionParams(dataSource);

        if (!dataSource.conditionParams)
            return;

        if (readNewData) {
            await dataSource.loadNewData(false);
        }
    }

    protected async _createConditionAndConditionParams(dataSource: IDataSource): Promise<void> {
        let condition = await this.createReaderCondition();
        let conditionParams = await this._createReaderConditionParams() ?? [];

        // nahrazení placeholderu @MASTER_ASSOC condition parametrem nesoucím hodnotu id master záznamu (index parametru je roven počtu aktuálně vytvořených parametrů)
        condition = StringUtils.replaceAll(condition, this._masterAssocParam.one, `@${conditionParams.length}`);
        // nahrazení placeholderu @MASTER_CLASS condition parametrem nesoucím název třídy master záznamu (index parametru je roven počtu aktuálně vytvořených parametrů + 1)
        condition = StringUtils.replaceAll(condition, this._masterClassParam.one, `@${conditionParams.length + 1}`);
        conditionParams = [...conditionParams, this._masterAssocParam.two];
        if (this.showSubRecords)
            conditionParams = [...conditionParams, this._masterClassParam.two];

        dataSource.condition = condition;
        dataSource.conditionParams = conditionParams;
    }

    protected async _showSubRecordsSupported(): Promise<boolean> {
        if (this.__showSubRecordsSupport)
            return this._showSubRecordsSupport;

        if (!this.masterFrame) {
            this._showSubRecordsSupport = false;
        } else {
            const objectName = this.masterFrame.objectName;
            let classDesc = await ModelDescriptionsManager.instance.getClassInfo(objectName);
            if (this.masterAttr) {
                const attr = await ModelDescriptionsManager.instance.getAttributeInfo(objectName, this.masterAttr);
                if (attr && attr.type === OWDataType.OWObject)
                    classDesc = await ModelDescriptionsManager.instance.getClassInfo(attr.additionalTypeName);
            }
            this._showSubRecordsSupport = classDesc.supportsHierarchy;
        }

        this.__showSubRecordsSupport = true;
        return this._showSubRecordsSupport;
    }

    protected async _addShowSubRecordsAction(): Promise<void> {
        if (await this._showSubRecordsSupported()) {
            this._showSubRecordsAction = FrameSharedMethods.createShowSubRecordsAction(this);
            this.menu.addAction(this._showSubRecordsAction);
        }
    }

    public override dispose(): void {
        this.conditionParamsAttrs = [];

        super.dispose();
    }

    protected _getExtendedProperties(): CaseInsensitiveMap<ExtendedPropertiesType> {
        const attributesRegex = /^(\w+)?Attributes/; // např. MultiSelectLOV.Attributes, ListAttributes
        const propertyNodes = [...this.container.children]
            .filter(element => element instanceof HTMLElement)
            .find(child => attributesRegex.test(child.getAttribute("data-tscitem")))?.children;

        return propertyNodes ? this._getExtendedPropertiesMap([...propertyNodes]?.map(element => element as HTMLElement)) : new CaseInsensitiveMap<ExtendedPropertiesType>();
    }

    private _getExtendedPropertiesMap(elements: HTMLElement[]): CaseInsensitiveMap<ExtendedPropertiesType> {
        const extendedProperties = new CaseInsensitiveMap<ExtendedPropertiesType>();
        elements?.forEach(element => {
            const properties = getExtendedPropertiesFromElement(element);
            const attributeName = element.getAttribute("data-attrname");
            extendedProperties.set(attributeName, properties);
        });

        return extendedProperties;
    }
}
